'use strict';

/**
 * DrawingEditor — an interactive Excalidraw canvas in a full-screen overlay,
 * built on pict-section-excalidraw's ReactView. A drawing is stored as a single
 * self-contained SVG Media blob in Parime: exportSvg({exportEmbedScene:true})
 * embeds the full scene (and image data) in the SVG <metadata>, and the vendor's
 * loadFromBlob re-extracts it — so one blob round-trips an editable drawing
 * (no file tree, no JSON sidecar).
 *
 * Opened via app.showDrawingEditor({ OwnerType, IDOwner, IDMedia }, onInsert):
 *   - IDMedia 0  → new drawing; on save uploads a Drawing Media owned by
 *                  (OwnerType, IDOwner) and calls onInsert({ URL, Title, IDMedia }).
 *   - IDMedia >0 → edit an existing drawing; loads its SVG, re-saves the bytes.
 *
 * The React vendor globals (window.PictSectionExcalidrawVendor) come from the
 * vendored <script> tags in index.html; LazyLoad* are configured as a fallback.
 */

const libExcalidraw = require('pict-section-excalidraw');
const libFrameworkClient = require('../providers/FrameworkClient.js');

const _OVERLAY_ID = 'Plansheet-DrawingOverlay';

// pict-section-excalidraw's vendored react-global-shim maps the automatic JSX
// runtime's jsx/jsxs (type, props, key) to window.React.createElement (type,
// props, ...children) whenever window.React lacks its own jsx/jsxs/jsxDEV --
// which it always does, since 'react' and 'react/jsx-runtime' are separate
// entry points and only the former gets loaded as a global. createElement's
// 3rd positional argument means "child", not "key", so every element Excalidraw
// builds inside a .map() (color swatches, the stroke-width radio group, etc.)
// has its real children silently clobbered by its own React key. Patching
// window.React with a correct jsx/jsxs before the vendor scripts run fixes it
// at the source without touching the vendored bundle.
function _ensureReactJsxRuntime(pReact)
{
	if (!pReact || pReact.jsx) { return pReact; }
	let tmpCreateElement = pReact.createElement;
	let tmpJsx = function (pType, pProps, pKey)
	{
		let tmpProps = pProps || {};
		if (pKey !== undefined) { tmpProps = Object.assign({}, tmpProps, { key: pKey }); }
		return tmpCreateElement(pType, tmpProps);
	};
	pReact.jsx = tmpJsx;
	pReact.jsxs = tmpJsx;
	pReact.jsxDEV = tmpJsx;
	return pReact;
}

if (typeof window !== 'undefined' && !window.__PlansheetReactJsxRuntimePatched)
{
	window.__PlansheetReactJsxRuntimePatched = true;
	if (window.React)
	{
		_ensureReactJsxRuntime(window.React);
	}
	else
	{
		let tmpReactValue;
		Object.defineProperty(window, 'React',
		{
			configurable: true,
			enumerable: true,
			get() { return tmpReactValue; },
			set(pValue) { tmpReactValue = _ensureReactJsxRuntime(pValue); }
		});
	}
}

// pict-section-excalidraw's react-vendor.min.js sets window.ReactDOM from
// 'react-dom/client' alone (createRoot/hydrateRoot/version only) -- it never
// loads the base 'react-dom' entry, so createPortal (and flushSync) are
// missing. Excalidraw's color-picker popover renders through a real
// ReactDOM.createPortal, so opening it throws "createPortal is not a
// function" and takes the whole canvas down. createPortal doesn't need any
// renderer internals: react-dom's own implementation is just this tagged
// descriptor object, recognized generically by the reconciler via its
// well-known $$typeof (Symbol.for('react.portal'), stable since React 16 --
// see react-dom's ReactPortal.js). Providing it here fixes the popover at
// the source without touching the vendored bundle.
function _ensureReactDomPortal(pReactDOM)
{
	if (!pReactDOM || pReactDOM.createPortal) { return pReactDOM; }
	let _REACT_PORTAL_TYPE = (typeof Symbol === 'function' && Symbol.for) ? Symbol.for('react.portal') : 0xeaca;
	pReactDOM.createPortal = function (pChildren, pContainer, pKey)
	{
		if (!pContainer || pContainer.nodeType !== 1 && pContainer.nodeType !== 9 && pContainer.nodeType !== 11)
		{
			throw new Error('Target container is not a DOM element.');
		}
		return {
			$$typeof: _REACT_PORTAL_TYPE,
			key: (pKey === null || pKey === undefined) ? null : String(pKey),
			children: pChildren,
			containerInfo: pContainer,
			implementation: null
		};
	};
	if (!pReactDOM.flushSync) { pReactDOM.flushSync = function (pFn) { return pFn(); }; }
	return pReactDOM;
}

if (typeof window !== 'undefined' && !window.__PlansheetReactDomPortalPatched)
{
	window.__PlansheetReactDomPortalPatched = true;
	if (window.ReactDOM)
	{
		_ensureReactDomPortal(window.ReactDOM);
	}
	else
	{
		let tmpReactDomValue;
		Object.defineProperty(window, 'ReactDOM',
		{
			configurable: true,
			enumerable: true,
			get() { return tmpReactDomValue; },
			set(pValue) { tmpReactDomValue = _ensureReactDomPortal(pValue); }
		});
	}
}

function buildConfiguration()
{
	return {
		ViewIdentifier: 'Plansheet-DrawingEditor',
		DefaultRenderable: 'DrawingEditor-Overlay',
		DefaultDestinationAddress: '#' + _OVERLAY_ID,
		TargetElementAddress: '#DrawingEditor-Canvas',
		EmbedMode: 'react',
		Theme: 'auto',
		AssetBaseURL: '/js/excalidraw-assets/',
		LazyLoadReactVendorURL: '/js/react-vendor.min.js',
		LazyLoadWrapperURL: '/js/excalidraw-wrapper.min.js',
		DrawingDataAddress: false,
		AutoRender: false,
		CSS: /*css*/`
			#${_OVERLAY_ID}:empty { display: none; }
			.ps-draw { position: fixed; inset: 0; z-index: 4000; display: flex; flex-direction: column;
				background: var(--theme-color-background-default, #f4f6fa); }
			.ps-draw-bar { display: flex; align-items: center; gap: 12px; padding: 10px 16px;
				border-bottom: 1px solid var(--theme-color-border-default, #e6e9ee);
				background: var(--theme-color-background-panel, #fff); }
			.ps-draw-bar .ps-draw-title { font-size: 15px; font-weight: 600; color: var(--theme-color-text-primary, #2c3140); }
			.ps-draw-bar input { flex: 1; max-width: 360px; }
			.ps-draw-spacer { flex: 1; }
			.ps-draw-canvas { flex: 1; min-height: 0; position: relative; }
			.ps-draw-canvas .pict-excalidraw-wrap, .ps-draw-canvas .pict-excalidraw-mount,
			.ps-draw-canvas .excalidraw, .ps-draw-canvas .excalidraw-container { height: 100%; width: 100%; }
		`,
		Templates:
		[
			{
				Hash: 'DrawingEditor-Overlay',
				Template: /*html*/`
<div class="ps-draw">
	<div class="ps-draw-bar">
		<span class="ps-draw-title">Drawing</span>
		<input class="ps-input" type="text" placeholder="Drawing title" value="{~D:AppData.DrawingEditor.Title~}" oninput="_Pict.views['Plansheet-DrawingEditor'].setTitle(this.value)">
		<span class="ps-draw-spacer"></span>
		<button class="ps-btn" onclick="_Pict.views['Plansheet-DrawingEditor'].cancel()">Cancel</button>
		<button class="ps-btn ps-btn-primary" onclick="_Pict.views['Plansheet-DrawingEditor'].saveAndInsert()">{~D:AppData.DrawingEditor.SaveLabel~}</button>
	</div>
	<div class="ps-draw-canvas" id="DrawingEditor-Canvas"></div>
</div>`
			}
		],
		Renderables:
		[
			{
				RenderableHash: 'DrawingEditor-Overlay',
				TemplateHash: 'DrawingEditor-Overlay',
				ContentDestinationAddress: '#' + _OVERLAY_ID
			}
		]
	};
}

class PictViewDrawingEditor extends libExcalidraw.ReactView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._ctx = null;
		this._onInsert = null;
		this.pict.AppData.DrawingEditor = this.pict.AppData.DrawingEditor || { Title: 'Drawing', SaveLabel: 'Save & insert' };
	}

	setTitle(pValue) { this.pict.AppData.DrawingEditor.Title = pValue; }

	open(pContext, fOnInsert)
	{
		// The excalidraw ReactView's destroy() is TERMINAL (it sets _destroyed and drops the React root),
		// and we reuse this one registered instance across opens. Without reviving it, the 2nd+ open renders
		// the overlay chrome but the excalidraw canvas + toolbar never mount (the mount lands in a half-dead
		// _destroyed state). Tear down any prior mount, then clear the terminal flag so this render mounts fresh.
		try { this.destroy(); } catch (pErr) { /* no prior mount */ }
		this._destroyed = false;
		this.initialRenderComplete = false;

		this._ctx = pContext || {};
		this._onInsert = (typeof fOnInsert === 'function') ? fOnInsert : null;
		// A routed opener (the Drawings page) passes onClose so cancel / save / close returns to /drawings;
		// the doc-embed picker passes none, so closing there just returns to the document underneath.
		this._onClose = (pContext && typeof pContext.onClose === 'function') ? pContext.onClose : null;
		this.pict.AppData.DrawingEditor.Title = this._ctx.Title || 'Drawing';
		// A standalone surface (the Drawings board) saves without "inserting" anywhere, so let the caller
		// relabel the primary button (default keeps the doc-editor's "Save & insert").
		this.pict.AppData.DrawingEditor.SaveLabel = this._ctx.SaveLabel || 'Save & insert';

		// Load an existing drawing's scene on mount, or start blank.
		if (this._ctx.IDMedia)
		{
			this.options.OnLoad = (pView, fCb) => this._loadExistingScene(this._ctx.IDMedia, fCb);
		}
		else
		{
			this.options.OnLoad = null;
		}

		// The overlay host (#Plansheet-DrawingOverlay) is part of the shell-rendered
		// DOM (PictView-Shell), so just render the editor into it (mounts React).
		this.render();
	}

	_loadExistingScene(pIDMedia, fCallback)
	{
		let tmpVendor = this._resolveVendor();
		if (!tmpVendor || !tmpVendor.loadFromBlob) { return fCallback(null, null); }
		libFrameworkClient.getText(this.pict, this.pict.providers.MediaAPI.blobURL(pIDMedia))
			.then((pSvg) => tmpVendor.loadFromBlob(new Blob([pSvg], { type: 'image/svg+xml' }), null, null))
			.then((pScene) => fCallback(null, { elements: (pScene && pScene.elements) || [], appState: (pScene && pScene.appState) || {}, files: (pScene && pScene.files) || {} }))
			.catch((pErr) => fCallback(pErr));
	}

	saveAndInsert()
	{
		this.exportSvg({ exportEmbedScene: true }).then((pSvgEl) =>
		{
			if (!pSvgEl) { return this._toast('Nothing to save.', 'info'); }
			let tmpSvg = new XMLSerializer().serializeToString(pSvgEl);
			let tmpBlob = new Blob([tmpSvg], { type: 'image/svg+xml' });
			let tmpTitle = this.pict.AppData.DrawingEditor.Title || 'Drawing';
			let tmpMedia = this.pict.providers.MediaAPI;
			let tmpCtx = this._ctx || {};

			let tmpPromise = tmpCtx.IDMedia
				? tmpMedia.updateBlob(tmpCtx.IDMedia, tmpBlob, 'image/svg+xml').then(() => ({ IDMedia: tmpCtx.IDMedia }))
				: tmpMedia.upload(tmpCtx.OwnerType || 'Vision', tmpCtx.IDOwner || 0, tmpBlob,
					{ MediaType: 'Drawing', MimeType: 'image/svg+xml', Title: tmpTitle, OriginalFileName: tmpTitle + '.excalidraw.svg' });

			tmpPromise.then((pRow) =>
			{
				let tmpID = pRow.IDMedia;
				let tmpURL = tmpMedia.blobURL(tmpID);
				let tmpOnInsert = this._onInsert;
				this.close();
				if (tmpOnInsert) { tmpOnInsert({ URL: tmpURL, Title: tmpTitle, IDMedia: tmpID }); }
			})
			.catch((pErr) => this._toast('Save failed: ' + (pErr.message || pErr), 'error'));
		})
		.catch((pErr) => this._toast('Export failed: ' + (pErr.message || pErr), 'error'));
	}

	cancel() { this.close(); }

	close()
	{
		// Fire the close hook once (a routed opener uses it to navigate back to its list route).
		let tmpOnClose = this._onClose;
		this._onClose = null;
		try { this.destroy(); } catch (pErr) { /* ignore */ }
		if (this.pict.ContentAssignment) { this.pict.ContentAssignment.assignContent('#' + _OVERLAY_ID, ''); }
		if (typeof tmpOnClose === 'function') { tmpOnClose(); }
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function')
		{
			tmpModal.toast(pMessage, { type: pType || 'info', duration: (pType === 'error') ? 6000 : 2500 });
		}
	}
}

module.exports = PictViewDrawingEditor;
module.exports.default_configuration = buildConfiguration();
